import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Tag } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { fmt } from "../lib/money";
import ActiveAffiliateWidget from "../components/ActiveAffiliateWidget";
import { useAuth } from "../context/AuthContext";
import { canSeeAffiliateUI } from "../lib/permissions";

export default function Checkout() {
  const { items, subtotal, refresh, discounts, discountTotal, clearDiscounts, getAffiliateRef, clearAffiliateRef } = useCart();
  const { user } = useAuth();
  const showAffiliate = canSeeAffiliateUI(user);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", line1: "", line2: "", city: "", state: "",
    postal_code: "0592", country: "Guyana", phone: "",
  });
  const [loading, setLoading] = useState(false);

  const freeShippingUnlocked = discounts.some((d) =>
    ["free_shipping", "fixed_plus_free_shipping", "percent_plus_free_shipping"].includes(d.discount_type)
  );
  const shippingPercentOff = Math.max(0, ...discounts.filter((d) => d.discount_type === "shipping_percent").map((d) => d.discount_value || 0));
  let shippingBase = freeShippingUnlocked || subtotal >= 20000 ? 0 : 2000;
  let shipping = shippingPercentOff > 0 ? Math.max(0, Math.round(shippingBase * (1 - Math.min(shippingPercentOff, 100) / 100))) : shippingBase;
  // Mirror backend safety net: if items net to zero after discounts, auto-waive shipping.
  const shippingAutoWaived = shipping > 0 && subtotal - discountTotal <= 0;
  if (shippingAutoWaived) shipping = 0;
  const total = Math.max(subtotal + shipping - discountTotal, 0);
  const bind = (k) => ({
    id: k,
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    className: "rounded-none border-black h-11",
    "data-testid": `checkout-${k}`,
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ref = getAffiliateRef();
      // Belt-and-suspenders: just before placing the order, re-attach the ref
      // to the user's server-side session so even if cookies/localStorage were
      // wiped between landing and now (e.g. Facebook in-app browser quirks),
      // the backend can still resolve tm_ref from the user record at checkout.
      if (ref) {
        try { await api.post("/session/affiliate", { ref }); } catch { /* ignore */ }
      }
      const { data } = await api.post("/orders", {
        shipping_address: form,
        discount_codes: discounts.map((d) => d.code),
        tm_ref: ref,
      });
      clearDiscounts();
      clearAffiliateRef();
      await refresh();
      toast.success("Order placed");
      navigate(`/orders/${data.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 text-center">
        <h1 className="font-display font-black text-4xl">Nothing to checkout.</h1>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 w-full">
      <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight">Checkout</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_420px] gap-6 sm:gap-8 lg:gap-12 mt-6 sm:mt-10 min-w-0">
        <div className="space-y-8">
          <section>
            <h2 className="font-display font-bold text-lg mb-4 uppercase tracking-wider">Shipping address</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="full_name">Full name</Label>
                <Input required {...bind("full_name")} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="line1">Address line 1</Label>
                <Input required {...bind("line1")} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="line2">Address line 2 (optional)</Label>
                <Input {...bind("line2")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input required {...bind("city")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state">State / Region</Label>
                <Input required {...bind("state")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="postal_code">Postal code</Label>
                <Input required {...bind("postal_code")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country">Country</Label>
                <Input required {...bind("country")} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input required {...bind("phone")} />
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-4 uppercase tracking-wider">Payment</h2>
            <div className="border border-dashed border-neutral-400 p-6 bg-neutral-50">
              <p className="text-sm font-semibold">Mock payment — no card needed.</p>
              <p className="text-xs text-neutral-600 mt-1">
                This is a demo checkout. Your order will be placed without charging anything.
              </p>
            </div>
          </section>
        </div>

        <aside className="bg-neutral-50 border border-neutral-200 p-4 sm:p-6 h-fit min-w-0">
          <h2 className="font-display font-black text-2xl mb-5">Your order</h2>
          {showAffiliate && <ActiveAffiliateWidget prominent />}
          <ul className="space-y-3 border-b border-neutral-300 pb-4 max-h-64 overflow-auto">
            {items.map((it) => (
              <li key={it.line_id} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">
                  {it.title} × {it.quantity}
                  {(it.size || it.color) && (
                    <span className="text-neutral-500 text-xs ml-1">
                      ({[it.size, it.color].filter(Boolean).join(" · ")})
                    </span>
                  )}
                </span>
                <span className="font-semibold">{fmt(it.price * it.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            {discounts.length > 0 && (
              <div className="pt-2 border-t border-neutral-300 space-y-1">
                {discounts.map((d) => (
                  <div
                    key={d.code}
                    data-testid={`checkout-discount-${d.code}`}
                    className="flex justify-between text-[#00A355]"
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Tag className="w-3 h-3 shrink-0" />
                      <code className="font-bold truncate">{d.code}</code>
                      <span className="text-neutral-500 truncate">· {d.seller_name}</span>
                    </span>
                    <span className="font-semibold">{d.discount_type === "free_shipping" ? "Free shipping" : d.discount_type === "shipping_percent" ? `${d.discount_value}% off shipping` : `− ${fmt(d.discount_amount)}`}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center gap-2">
              <span>Shipping</span>
              <span className="text-right">
                {shipping === 0 ? "Free" : fmt(shipping)}
                {shippingAutoWaived && (
                  <span data-testid="checkout-shipping-auto-waived" className="block text-[10px] text-[#00A355] font-semibold uppercase tracking-wide">
                    Auto-applied
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-neutral-300 font-bold text-base">
              <span>Total</span><span data-testid="checkout-total">{fmt(total)}</span>
            </div>
          </div>
          <Button
            data-testid="place-order-btn"
            type="submit"
            disabled={loading}
            className="w-full mt-6 h-12 rounded-none bg-[#0033FF] hover:bg-[#0022CC] font-semibold"
          >
            {loading ? "Placing…" : "Place order"}
          </Button>
        </aside>
      </form>
    </div>
  );
}
